import { useEffect, useRef, useState } from 'react';
import { Camera } from 'lucide-react';
import { useCamera } from '@/components/camera';
import { Btn } from '@/components/UI';

export function ArtworkCamera({ onPhoto, onClose }) {
  const video = useRef(null);
  const [device, setDevice] = useState('');
  const { state, devices } = useCamera(video, device);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    const hide = () => { if (document.hidden) onClose(); };
    document.addEventListener('visibilitychange', hide);
    return () => document.removeEventListener('visibilitychange', hide);
  }, [onClose]);
  const capture = () => {
    const source = video.current;
    if (!source?.videoWidth || busy) return;
    setBusy(true); setError('');
    const canvas = document.createElement('canvas');
    const scale = Math.min(1, 2000 / Math.max(source.videoWidth, source.videoHeight));
    canvas.width = Math.round(source.videoWidth * scale); canvas.height = Math.round(source.videoHeight * scale);
    try {
      canvas.getContext('2d').drawImage(source, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(blob => {
        setBusy(false);
        if (blob) onPhoto(new File([blob], 'card-camera.jpg', { type: 'image/jpeg' }));
        else setError('Could not take the photo. Please try again or choose an image.');
      }, 'image/jpeg', .94);
    } catch { setBusy(false); setError('Could not take the photo. Please try choosing an image.'); }
  };
  return <section aria-label="Artwork camera" className="space-y-3 rounded-2xl border bg-slate-50 p-3">
    <p className="text-sm">Keep all four corners visible. Avoid glare; you can crop and correct perspective next. Nothing is uploaded until you save.</p>
    <video ref={video} muted autoPlay playsInline className="max-h-[45dvh] w-full rounded-xl bg-slate-900 object-contain" aria-label="Live card camera" />
    {state === 'denied' && <p role="alert">Camera access was denied or no camera is available. Allow camera access in your browser, or choose a file below.</p>}
    {state === 'unsupported' && <p role="alert">Live camera is unavailable in this browser. Use the phone camera/file option below.</p>}
    {state === 'idle' && <p role="status">Starting camera…</p>}
    {devices.length > 1 && <label className="block text-sm">Camera<select aria-label="Camera" value={device} onChange={event => setDevice(event.target.value)} className="ml-2 rounded-xl border p-2"><option value="">Rear camera (preferred)</option>{devices.map((entry, index) => <option key={entry.deviceId} value={entry.deviceId}>{entry.label || `Camera ${index + 1}`}</option>)}</select></label>}
    {error && <p role="alert">{error}</p>}
    <div className="flex gap-2"><Btn disabled={state !== 'ready' || busy} onClick={capture}><Camera className="h-4 w-4" />Capture photo</Btn><Btn variant="ghost" onClick={onClose}>Close camera</Btn></div>
  </section>;
}
